/** Resumen agrupado de los errores de una carga masiva.
 *
 * Pedido de Eduardo (2026-08-01): "me dice 35 errores, pero que ayude a
 * entender qué errores fueron... en columna 'xxx' cuántos jugadores". Antes
 * el mensaje era solo el CONTEO, así que con 35 fallos el admin no sabía si
 * era una columna mal llenada o 35 problemas distintos.
 */

export interface BulkUploadError {
  row?: string | number | null;
  error?: string | null;
  /** "missing_required" | "rejected" (aditivo del backend). */
  reason?: string | null;
  /** Columnas involucradas cuando el motivo las conoce. */
  fields?: string[] | null;
}

export interface BulkErrorGroup {
  /** Texto listo para pintar: "Falta «Nombre»" / "Correo duplicado". */
  label: string;
  /** Cuántas filas cayeron en este grupo. */
  count: number;
  /** Ejemplos de filas afectadas (para que el admin sepa dónde buscar). */
  samples: string[];
}

const COLUMN_LABELS: Record<string, string> = {
  id: "ID",
  first_name: "Nombre",
  last_name: "Apellido paterno",
  maternal_last_name: "Apellido materno",
  display_name: "Nombre para mostrar",
  unique_player_id: "ID único de jugador",
  phone: "Teléfono",
  email: "Correo",
  gender: "Género",
  birthdate: "Fecha de nacimiento",
  nationality_code: "Nacionalidad",
  national_federation_id: "ID federación",
  wtn_tennis_id: "WTN / Tennis ID",
  dominant_hand: "Mano dominante",
  backhand_type: "Revés",
  club_name: "Club",
  state_province: "Estado",
  city: "Ciudad",
  guardian_name: "Tutor",
  guardian_phone: "Teléfono del tutor",
  guardian_email: "Correo del tutor",
  guardian_relationship: "Parentesco del tutor",
  coach_name: "Entrenador",
  coach_phone: "Teléfono del entrenador",
  coach_email: "Correo del entrenador",
  category_id: "Categoría",
  data_consent: "Consentimiento de datos",
  notes: "Notas",
};

export function bulkColumnLabel(field: string): string {
  return COLUMN_LABELS[field] ?? field;
}

const MAX_SAMPLES = 3;

/** Agrupa por CAUSA: una fila a la que le faltan 2 columnas cuenta en ambas,
 * porque el admin necesita saber cuántos jugadores arreglar por columna. Los
 * rechazos sin columna conocida se agrupan por su mensaje literal. */
export function summarizeBulkErrors(errors: BulkUploadError[]): BulkErrorGroup[] {
  const groups = new Map<string, BulkErrorGroup>();

  const push = (key: string, label: string, row: BulkUploadError["row"]) => {
    const group = groups.get(key) ?? { label, count: 0, samples: [] };
    group.count += 1;
    const sample = (row ?? "").toString().trim();
    if (sample && sample !== "?" && group.samples.length < MAX_SAMPLES) {
      group.samples.push(sample);
    }
    groups.set(key, group);
  };

  for (const entry of errors) {
    const fields = entry.fields ?? [];
    if (entry.reason === "missing_required" && fields.length > 0) {
      for (const field of fields) {
        push(`missing:${field}`, `Falta «${bulkColumnLabel(field)}»`, entry.row);
      }
      continue;
    }
    // Sin columna conocida: el mensaje del backend ES la causa.
    const message = (entry.error ?? "").trim() || "Error sin detalle";
    push(`message:${message}`, message, entry.row);
  }

  // Primero lo que más filas afecta: es lo que más rinde arreglar.
  return [...groups.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
}

/** Una línea por grupo, lista para el mensaje de resultado. */
export function bulkErrorGroupLine(group: BulkErrorGroup): string {
  const jugadores = group.count === 1 ? "1 jugador" : `${group.count} jugadores`;
  const ejemplos = group.samples.length > 0 ? ` (ej. ${group.samples.join(", ")})` : "";
  return `${group.label}: ${jugadores}${ejemplos}`;
}

const MAX_MISSING_IDS = 5;

/** Misma queja de Eduardo aplicada a la carga por identificadores: "N IDs
 * sin match" no dice CUÁLES, y el backend ya devuelve las listas. Una
 * línea por tipo de ID, enumerando hasta MAX_MISSING_IDS y resumiendo el
 * resto — así el admin corrige el layout sin adivinar. */
export function missingIdentifierLines(
  missingFmtIds: string[],
  missingWtnIds: string[],
): string[] {
  const lines: string[] = [];
  const format = (label: string, ids: string[]) => {
    if (ids.length === 0) return;
    const shown = ids.slice(0, MAX_MISSING_IDS);
    const rest = ids.length - shown.length;
    lines.push(
      `${label} sin match: ${shown.join(", ")}${rest > 0 ? ` (+${rest} más)` : ""}`,
    );
  };
  format("ID FMT", missingFmtIds);
  format("WTN ID", missingWtnIds);
  return lines;
}
